<template>
  <div class="specialization-management">
    <div>
      <div class="content-container">
        <div class="header-section">
          <h1>Specialization Management</h1>
          <div class="action-buttons">
            <button
              v-if="$canAccess('SpecializationAdd')"
              class="btn btn-primary"
              @click="$router.push({ name: 'SpecializationAdd' })"
            >
              <i class="fas fa-plus-circle"></i> Add Specialization
            </button>
            <button
              class="btn btn-secondary"
              @click="$router.push({ name: 'nurses' })"
            >
              <i class="fas fa-arrow-left"></i> Back to Nurses
            </button>
          </div>
        </div>

        <!-- Loading Indicator -->
        <div v-if="loading" class="loading-container">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <!-- Specializations Table -->
        <div v-if="!loading && specializations && specializations.length > 0" class="specialization-table-container">
          <div class="table-filter mb-3">
            <input
              type="text"
              class="form-control"
              v-model="searchTerm"
              placeholder="Search specializations..."
            >
          </div>

          <table class="table table-striped table-hover table-bordered">
            <thead>
              <tr>
                <th @click="sortBy('name')">
                  Name 
                  <i class="fas fa-sort"></i>
                </th>
                <th @click="sortBy('code')">
                  Code
                  <i class="fas fa-sort"></i>
                </th>
                <th @click="sortBy('description')">
                  Description
                  <i class="fas fa-sort"></i>
                </th>
                <th @click="sortBy('certified_nurses_count')">
                  Certified Nurses
                  <i class="fas fa-sort"></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="specialization in paginatedSpecializations" :key="specialization.id">
                <td>{{ specialization.name }}</td>
                <td>{{ specialization.code }}</td>
                <td>{{ specialization.description || 'No description provided' }}</td>
                <td>{{ specialization.certified_nurses_count || 0 }}</td>
                <td>
                  <div class="btn-group">
                    <button
                      v-if="$canAccess('SpecializationEdit')"
                      class="btn btn-sm btn-outline-primary"
                      @click="editSpecialization(specialization.id)"
                    >
                      Edit
                    </button>
                    <button
                      v-if="$canAccess('SpecializationView')"
                      class="btn btn-sm btn-outline-info"
                      @click="viewSpecializationDetails(specialization.id)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="pagination-container">
            <nav>
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="setPage(currentPage - 1)">Previous</button>
                </li>
                <li 
                  v-for="page in totalPages" 
                  :key="page" 
                  class="page-item"
                  :class="{ active: page === currentPage }"
                >
                  <button class="page-link" @click="setPage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="setPage(currentPage + 1)">Next</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && (!specializations || specializations.length === 0)" class="empty-state">
          <div class="empty-state-content">
            <i class="fas fa-stethoscope fa-3x"></i>
            <h3>No Specializations Found</h3>
            <p>There are no specializations in the system yet. Get started by adding a new specialization.</p>
            <button
              v-if="$canAccess('SpecializationAdd')"
              class="btn btn-primary"
              @click="$router.push({ name: 'SpecializationAdd' })"
            >
              <i class="fas fa-plus-circle"></i> Add Specialization
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpecializationList',
  data() {
    return {
      specializations: [],
      loading: false,
      error: null,
      searchTerm: '',
      sortColumn: 'name',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    filteredSpecializations() {
      if (!this.specializations) return [];
      
      let filtered = [...this.specializations];
      
      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(specialization => 
          specialization.name.toLowerCase().includes(searchLower) ||
          specialization.code.toLowerCase().includes(searchLower) ||
          (specialization.description && specialization.description.toLowerCase().includes(searchLower))
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let valueA = a[this.sortColumn];
        let valueB = b[this.sortColumn];
        
        // Handle null values
        if (valueA === null) valueA = '';
        if (valueB === null) valueB = '';
        
        // String comparison
        if (typeof valueA === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }
        
        // Date comparison
        if (this.sortColumn === 'created_at') {
          valueA = new Date(valueA);
          valueB = new Date(valueB);
        }
        
        // Numeric comparison for counts
        if (this.sortColumn === 'certified_nurses_count') {
          valueA = valueA || 0;
          valueB = valueB || 0;
        }
        
        // Do the actual comparison
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    },
    paginatedSpecializations() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredSpecializations.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredSpecializations.length / this.itemsPerPage);
    }
  },
  methods: {
    async fetchSpecializations() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get('/specializations');
        this.specializations = response.data.data;
      } catch (error) {
        console.error('Error fetching specializations:', error);
        this.error = 'Failed to load specializations. Please try again later.';
        this.specializations = [];
      } finally {
        this.loading = false;
        // this.$toast.success('Specialization created successfully!')
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if already sorting by this column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for new column
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    setPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    editSpecialization(specializationId) {
      this.$router.push({
        name: 'SpecializationEdit',
        params: {id: specializationId}
      });
    },
    viewSpecializationDetails(specializationId) {
      this.$router.push({
        name: 'SpecializationView',
        params: { id: specializationId } 
      });
    }
  },
  mounted() {
    this.fetchSpecializations();
  }
}
</script>

<style scoped>
.table td {
  text-align: left;
}

.specialization-management {
  padding: 20px;
}

.content-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  width: 100%;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.table-filter {
  display: flex;
  gap: 15px;
}

.table-filter input {
  max-width: 300px;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.empty-state-content {
  text-align: center;
  max-width: 500px;
}

.empty-state-content i {
  color: #ccc;
  margin-bottom: 10px;
}

.empty-state-content h3 {
  margin-bottom: 10px;
}

.empty-state-content p {
  margin-bottom: 20px;
  color: #666;
}

th {
  cursor: pointer;
}

th i {
  margin-left: 5px;
  opacity: 0.5;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .table-filter {
    flex-direction: column;
  }
  
  .input-group {
    max-width: 100%;
  }
}
</style>
